import React, { useState, useEffect } from "react";
import {
  Box, VStack, Heading, Text, useColorModeValue, Button,
  HStack, useBreakpointValue, useToast, Spinner, Divider
} from "@chakra-ui/react";
import axios from "axios";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { StudentInfo, ExamAttemptListing, ExamAttemptDetails, ExamAttemptScore,ExamAttemptAnalytics } from "../Common/types";
import QuestionSetViewAttempt from "../Components/QuestionSetViewAttempt";
import ScoreViewer from "../Components/ScoreViewer";
import QuestionSetAnalytics from "../Components/QuestionSetAnalytics";

const StudentScoreViewAttempt = () => {
  const [currentStudent, setCurrentStudent] = useState<StudentInfo>();
  const [examAttemptListing, setExamAttemptListing] = useState<ExamAttemptListing>();
  const [examAttemptScore, setExamAttemptScore] = useState<ExamAttemptScore | null>(null);
  const [examAttemptDetails, setExamAttemptDetails] = useState<ExamAttemptDetails>();
  const [examAttemptAnalytics, setExamAttemptAnalytics] = useState<ExamAttemptAnalytics | null>(null);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [viewMode, setViewMode] = useState<"score" | "analytics" | "attempt">("score");

  const { exam_attempt_id } = useParams<{ exam_attempt_id: string }>();
  const token = localStorage.getItem("token");
  const isAuthenticated = Boolean(token);
  const navigate = useNavigate();
  const toast = useToast();
  const cardBg = useColorModeValue("blue.50", "blue.700");
  const textColor = useColorModeValue("blue.800", "blue.50");
  const bannerColor = useColorModeValue("yellow.100", "yellow.700");
  const showBanner = useBreakpointValue({ base: false, md: true });



  const cardBgDefault = useColorModeValue("gray.100", "gray.700");
const cardBgHover = useColorModeValue("gray.200", "gray.600");
const cardTextColor = useColorModeValue("gray.800", "gray.100");
const cardBorderColor = useColorModeValue("gray.300", "gray.600");



  const fetchCurrentStudent = async () => {
    try {
      const apiurl = process.env.REACT_APP_API_URL || "https://default-url.com";
      const response = await axios.get(`${apiurl}/students-info`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCurrentStudent(response.data);
    } catch (error) {
      console.error("Error fetching Student Info:", error);
    }
  };

  const fetchExamAttemptListing = async () => {
    const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";
    try {
      const response = await axios.get(`${apiUrl}/student/${exam_attempt_id}/exam-attempt-listing/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setExamAttemptListing(response.data);
      console.log("Exam Attempt Listing", response.data);
    } catch (error) {
      console.error("Failed to load exam attempt listings", error);
    }
  };

  const fetchExamScore = async () => {
    try {
      setIsLoading(true);
      const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";
      const response = await axios.get(
        `${apiUrl}/exam-attempt/${exam_attempt_id}/score`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setExamAttemptScore(response.data);
      console.log("Exam Attempt Score", response.data);
    } catch (error) {
      toast({
        title: "Failed to fetch exam score",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchExamAnalytics = async () => {
      try {
        setIsLoading(true);
        const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";
        const response = await axios.get(
          `${apiUrl}/exam-attempt/${exam_attempt_id}/analytics`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setExamAttemptAnalytics(response.data);
        console.log("Exam Attempt Analytics", response.data);
      } catch (error) {
        toast({
          title: "Failed to fetch exam analytics",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setIsLoading(false);
      }
    };

  const fetchExamAttemptDetails = async (exam_attempt_id: number) => {
    if (examAttemptDetails) {
      setViewMode("attempt");
      return;
    }
      if (window.confirm("Are you sure to view complete attempt details? Please note your remaining view count will be updated accordingly")) {

                setIsLoading(true);
                const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";
                try {
                const response = await axios.get(`${apiUrl}/exam-attempt/${exam_attempt_id}/post-attempt-details`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setExamAttemptDetails(response.data);
                 console.log("Exam Attempt Details", response.data);
                  await fetchExamAttemptListing();
                setViewMode("attempt");
                } catch (error) {
                console.error("Failed to load exam attempt listings", error);
                toast({
                    title: "Failed to fetch detailed attempt",
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });
                } finally {
                setIsLoading(false);
                }
            }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchCurrentStudent();
      fetchExamScore();
      fetchExamAttemptListing();
      fetchExamAnalytics();
    }
  }, [exam_attempt_id]);

  const getButtonProps = (examAttemptListing: ExamAttemptListing) => {
    const completed = examAttemptListing.status === "COMPLETED";
    if (completed) {
      if (examAttemptListing.view_count >= examAttemptListing.max_view_limit) {
        return {
          label: "Detailed Attempt View Count Exhausted",
          color: "gray",
          isDisabled: true,
        };
      } else {
        const viewCountRemaining = examAttemptListing.max_view_limit - examAttemptListing.view_count;
        return {
          label: `Detailed Attempt View Count Remaining : ${viewCountRemaining}`,
          color: "blue",
          isDisabled: false,
        };
      }
    }
    return {
      label: "In-Progress or Disqualified Attempt",
      color: "gray",
      isDisabled: true,
    };
  };

  return (
    <Box>
      {showBanner ? (
        <Box bgColor={bannerColor} p={4} height="70px">
          <HStack>
            <Heading size="sm">Hello {currentStudent?.givenName}!</Heading>
            <Text fontSize="md">Checkout your exam results.</Text>
          </HStack>
        </Box>
      ) : (
        <Box p={4} bgColor={bannerColor} height="50px">
          <VStack>
            <Text fontWeight="semibold" fontSize="md">My Exam Results</Text>
          </VStack>
        </Box>
      )}

      <Box mx="auto" color={textColor} p={4}>
        <HStack mb={4} justifyContent="space-between">
          <Heading size="md">Exam Scores</Heading>
          {examAttemptListing && (
            <HStack spacing={4}>
              <Button
                colorScheme="green"
                size="sm"
                onClick={() => setViewMode("score")}
                isDisabled={viewMode === "score"}
              >
                View Score
              </Button>
                {examAttemptAnalytics && (
                    <Button
                      colorScheme="purple"
                      size="sm"
                      onClick={() => setViewMode("analytics")}
                      isDisabled={viewMode === "analytics"}
                    >
                      View Analytics
                    </Button>
                  )}
              <Button
                colorScheme={getButtonProps(examAttemptListing).color}
                size="sm"
                onClick={() => fetchExamAttemptDetails(examAttemptListing.exam_attempt_id)}
                isDisabled={viewMode === "attempt" || getButtonProps(examAttemptListing).isDisabled}
              >
                {getButtonProps(examAttemptListing).label}
              </Button>
            </HStack>
          )}
        </HStack>

        {viewMode === "score" && examAttemptScore && (
        <Box
          p={2}
          borderRadius="sm"
          boxShadow="md"
          border="1px solid"
          borderColor="gray.200"
        >
            <ScoreViewer scoreData={examAttemptScore} />
          
        </Box>
        )}
        
                {isLoading ? (
          <Spinner size="xl" />
        ) : (
          viewMode === "analytics" && (
                <Box
       
            borderRadius="sm"
            boxShadow="md"
            border="1px solid"
            borderColor="gray.200"
            >
              <HStack p={4} spacing={4}  >
                {examAttemptAnalytics?.attempted_question_set_analytics.map((qs, idx) => (
                    <Box
                        key={qs.exam_attempted_question_set_id}
                        p={2}
                        px={4}
                        borderRadius="md"
                        minW="160px"
                        cursor="pointer"
                        bg={selectedIndex === idx ? "blue.600" : cardBgDefault}
                        color={selectedIndex === idx ? "white" : cardTextColor}
                        borderWidth="1px"
                        borderColor={selectedIndex === idx ? "blue.800" : cardBorderColor}
                        onClick={() => setSelectedIndex(idx)}
                        _hover={{
                            bg: selectedIndex === idx ? "blue.700" : cardBgHover,
                            transform: "translateY(-2px)",
                            boxShadow: "md"
                        }}
                        transition="all 0.2s ease"
                        textAlign="center"
                        >
                        <Text fontWeight="semibold" fontSize="sm" isTruncated>
                            {qs.title}
                        </Text>

                        </Box>

                ))}
              </HStack>
              <Divider />
              <Box position="relative" minH="500px">
                {examAttemptAnalytics?.attempted_question_set_analytics.map((qs, idx) => (
                  <Box
                    key={qs.exam_attempted_question_set_id}
                    display={selectedIndex === idx ? "block" : "none"}
                    transition="opacity 0.2s ease"
                  >
                    <QuestionSetAnalytics examAttemptedQsetAnalyticsProp={qs} />
                  </Box>
                ))}
              </Box>
            </Box>
          )
        )}

        {isLoading ? (
          <Spinner size="xl" />
        ) : (
          viewMode === "attempt" && (
          //  <Box overflowX="auto" pb={2}>
                <Box
       
            borderRadius="sm"
            boxShadow="md"
            border="1px solid"
            borderColor="gray.200"
            >
              <HStack p={4} spacing={4}  >
                {examAttemptDetails?.question_sets.map((qs, idx) => (
                    <Box
                        key={qs.exam_attempted_question_set_id}
                        p={2}
                        px={4}
                        borderRadius="md"
                        minW="160px"
                        cursor="pointer"
                        bg={selectedIndex === idx ? "blue.600" : cardBgDefault}
                        color={selectedIndex === idx ? "white" : cardTextColor}
                        borderWidth="1px"
                        borderColor={selectedIndex === idx ? "blue.800" : cardBorderColor}
                        onClick={() => setSelectedIndex(idx)}
                        _hover={{
                            bg: selectedIndex === idx ? "blue.700" : cardBgHover,
                            transform: "translateY(-2px)",
                            boxShadow: "md"
                        }}
                        transition="all 0.2s ease"
                        textAlign="center"
                        >
                        <Text fontWeight="semibold" fontSize="sm" isTruncated>
                            {qs.question_set_title}
                        </Text>
                        <Text fontSize="xs">
                            Total: {qs.attempted_question_details.length} questions
                        </Text>
                        </Box>

                ))}
              </HStack>
              <Divider />
              <Box position="relative" minH="500px">
                {examAttemptDetails?.question_sets.map((qs, idx) => (
                  <Box
                    key={qs.exam_attempted_question_set_id}
                    display={selectedIndex === idx ? "block" : "none"}
                    transition="opacity 0.2s ease"
                  >
                    <QuestionSetViewAttempt examAttemptedQuestionSetProp={qs.attempted_question_details} />
                  </Box>
                ))}
              </Box>
            </Box>
          )
        )}
      </Box>
    </Box>
  );
};

export default StudentScoreViewAttempt;
